'use strict';

var defaults = require('../defaults/Defaults');
var FirebaseExt = require('./FirebaseExt');
var FirebaseObject = require('./FirebaseObject');
var KayleeBase = require('./KayleeBase');
var KayleeCache = require('./KayleeCache');
var KayleeCalendar = require('./KayleeCalendar');
var KayleeCall = require('./KayleeCall');
var KayleeCalls = require('./KayleeCalls');
var KayleeConnectionState = require('./KayleeConnectionState');
var KayleeControllers = require('./KayleeControllers');
var KayleeController = require('./KayleeController');
var KayleeDirectories = require('./KayleeDirectories');
var KayleeDirectory = require('./KayleeDirectory');
var KayleeEquipment = require('./KayleeEquipment');
var KayleeEquipments = require('./KayleeEquipments');
var KayleeEvent = require('./KayleeEvent');
var KayleeEvents = require('./KayleeEvents');
var KayleeOffice365 = require('./KayleeOffice365');
var KayleeRemoteControlSessions = require('./KayleeRemoteControlSessions');
var KayleeRoomApiKeys = require('./KayleeRoomApiKeys');
var KayleeRoomControlCommands = require('./KayleeRoomControlCommands');
var KayleeRoomDiscoveredEquipment = require('./KayleeRoomDiscoveredEquipment');
var KayleeUsers = require('./KayleeUsers');
var KayleeUtil = require('./KayleeUtil');
var log = require('../log/Log').channel('KayleeRoom');
var sutl = require('../sutl/Sutl');

var KayleeRoom = {
    addUpdatesToCreateRoom: addUpdatesToCreateRoom,
    addUpdatesToRemoveRoom: addUpdatesToRemoveRoom,
    fromJson: fromJson,
    getRoom: getRoom,
    // resetRemoteControlCode is only for use by Jayne.
    // Inara should use room.$resetRemoteControlCode
    resetRemoteControlCode: resetRemoteControlCode,
};

function addUpdatesToCreateRoom(update, companyId, siteId, roomId, name) {
    update['rooms/' + roomId] = {
        companyId: companyId,
        name: name,
        siteId: siteId,
    };

    update['index/roomsBySite/' + siteId + '/' + roomId] = true;

    return KayleeUtil.loadUsersBySiteIndex({ siteId: siteId }, false)
        .then(function (obj) {
            // Add all users with explicit roles for the parent company or parent
            // site to the usersByRoom index for this room.

            var userPromises = [];
            var addUserRole = function (obj) {
                if (
                    sutl.nodeExists(obj.user, ['roles', 'companies', companyId, 'role']) ||
                    sutl.nodeExists(obj.user, [
                        'roles',
                        'companies',
                        companyId,
                        'sites',
                        siteId,
                        'role',
                    ])
                ) {
                    update['index/usersByRoom/' + roomId + '/' + obj.userId] = true;
                }
            };

            for (var userId in obj.usersBySite) {
                if (!obj.usersBySite.hasOwnProperty(userId)) continue;
                userPromises.push(KayleeUtil.loadUserData({ userId: userId }).then(addUserRole));
            }

            return Promise.all(userPromises);
        })
        .then(function () {
            return KayleeEvent.addUpdatesForRoomCreatedEvent(update, siteId, roomId, name);
        });
}

function addUpdatesToRemoveRoom(update, companyId, siteId, roomId, parentEventId) {
    update['rooms/' + roomId] = null;
    update['roomApiKeys/' + roomId] = null;
    update['roomConfig/' + roomId] = null;
    update['roomConfigValidated/' + roomId] = null;
    update['index/roomsBySite/' + siteId + '/' + roomId] = null;
    update['index/usersByRoom/' + roomId] = null;
    update['calendars/' + roomId] = null;
    update['directories/' + roomId] = null;
    update['roomDiscoveredEquipment/' + roomId] = null;
    update['roomLicenses/' + roomId] = null;

    return KayleeEvent.addUpdatesForRoomDeletedEvent(update, roomId, parentEventId)
        .then(function () {
            if (typeof parentEventId === 'undefined' || parentEventId === null) {
                parentEventId = KayleeEvent.getEventIdFromUpdates(update);

                if (parentEventId === null) {
                    return Promise.reject("couldn't get event id");
                }
            }

            return KayleeUtil.loadUsersByRoomIndex({ roomId: roomId }, false);
        })
        .then(function (obj) {
            // Remove room roles for any users who have them.

            var userRolePromises = [];
            var removeUserRole = function (obj) {
                if (
                    sutl.nodeExists(obj.user, [
                        'roles',
                        'companies',
                        companyId,
                        'sites',
                        siteId,
                        'rooms',
                        roomId,
                        'role',
                    ])
                ) {
                    update[
                        'users/' +
                            obj.userId +
                            '/roles/companies/' +
                            companyId +
                            '/sites/' +
                            siteId +
                            '/rooms/' +
                            roomId +
                            '/role'
                    ] = null;
                    return KayleeEvent.addUpdatesForRoleDeletedEvent(
                        update,
                        obj.userId,
                        obj.user.roles.companies[companyId].sites[siteId].rooms[roomId].role,
                        companyId,
                        siteId,
                        roomId,
                        KayleeBase.app.auth().currentUser
                            ? KayleeBase.app.auth().currentUser.uid
                            : null,
                        parentEventId
                    );
                }

                return update;
            };

            for (var userId in obj.usersByRoom) {
                if (!obj.usersByRoom.hasOwnProperty(userId)) continue;

                userRolePromises.push(
                    KayleeUtil.loadUserData({ userId: userId }).then(removeUserRole)
                );
            }

            return Promise.all(userRolePromises);
        })
        .then(function () {
            return KayleeUtil.loadEquipmentByRoomIndex({ roomId: roomId }, false);
        })
        .then(function (obj) {
            // Remove equipment for the room.

            var removeEquipmentPromises = [];

            for (var equipmentId in obj.equipmentByRoom) {
                if (!obj.equipmentByRoom.hasOwnProperty(equipmentId)) continue;

                removeEquipmentPromises.push(
                    KayleeEquipment.addUpdatesToRemoveEquipment(
                        update,
                        roomId,
                        equipmentId,
                        parentEventId
                    )
                );
            }

            return Promise.all(removeEquipmentPromises);
        })
        .then(function () {
            // If an equipment is configured as the calendarSource, it deletes the
            // settings/calendarSource entry when it is removed. That causes a
            // problem when we're deleting the entire room since we already have
            // an update entry for the room. To work around this, we just delete
            // the update for removing the calendarSource.

            delete update['rooms/' + roomId + '/settings/calendarSource'];
            return Promise.resolve();
        })
        .then(function () {
            return KayleeUtil.loadControllersByRoomIndex({ roomId: roomId }, false);
        })
        .then(function (obj) {
            // Unpair controllers from the room.

            var unpairControllerPromises = [];

            for (var controllerId in obj.controllersByRoom) {
                if (!obj.controllersByRoom.hasOwnProperty(controllerId)) continue;

                unpairControllerPromises.push(
                    KayleeController.addUpdatesToUnpairController(
                        update,
                        roomId,
                        controllerId,
                        parentEventId,
                        false
                    )
                );
            }

            return Promise.all(unpairControllerPromises);
        })
        .then(function () {
            return KayleeUtil.loadCallsByRoomIndex({ roomId: roomId }, false);
        })
        .then(function (obj) {
            // Remove calls for the room.

            var removeCallPromises = [];

            for (var callId in obj.callsByRoom) {
                if (!obj.callsByRoom.hasOwnProperty(callId)) continue;

                removeCallPromises.push(KayleeCall.addUpdatesToRemoveCall(update, roomId, callId));
            }

            return Promise.all(removeCallPromises);
        })
        .then(function () {
            return KayleeRemoteControlSessions.getRemoteControlSessions(roomId).$loaded();
        })
        .then(function (sessions) {
            if (sessions.length <= 0) return;

            for (var i = 0; i < sessions.length; i++) {
                var session = sessions[i];
                update['remoteControlSessions/' + session.$id] = null;
                update['index/remoteControlSessionsByRoom/' + roomId + '/' + session.$id] = null;

                if (session.userId !== 'anonymousRemoteControl') {
                    update['index/remoteControlSessionsByUser/' + session.userId] = null;
                }
            }
        })
        .then(function () {
            return update;
        });
}

function fromJson(data) {
    var reqProps = ['$id'];
    return FirebaseObject.$fromJson(data, 'Room', reqProps, reqProps, function (data) {
        return getRoom(data.$id);
    });
}

function getRoom(roomId) {
    var key = 'Room: ' + roomId;
    var room = KayleeCache.get(key);

    if (room === null) {
        room = new Room(roomId);
        KayleeCache.put(key, room);
    }

    return room;
}

function resetRemoteControlCode(roomId, currentCode) {
    // TODO: consider making this a series of smaller updates to specific parts
    // of the database rather than one large update to the entire database.

    return KayleeBase.app
        .database()
        .ref('index/roomsByRemoteControlCode')
        .once('value')
        .then(function (snap) {
            var codeIndex = snap.exists() ? snap.val() : {};
            var codes = Object.keys(codeIndex);
            var codeLength = 12;
            var code = sutl.generateAlphanumericCode(codeLength);
            var updates = {};

            while (codes.includes(code)) {
                code = sutl.generateAlphanumericCode(codeLength); // prevent duplicates
            }

            // If there's already an entry for this room, delete it.

            if (currentCode !== null && typeof currentCode !== 'undefined') {
                updates['index/roomsByRemoteControlCode/' + currentCode] = null;
            }

            updates['index/roomsByRemoteControlCode/' + code] = roomId;

            // We're not using room.$save since we need to update the index atomically when we update
            // the remote control code.

            updates['rooms/' + roomId + '/remoteControl'] = {
                code: code,
                time: Date.now(),
            };

            return KayleeBase.update(updates);
        });
}

function Room(roomId) {
    this.$nodeType = 'room';
    this.$commandsRef = KayleeBase.app.database().ref('roomControlCommands/' + roomId);
    this.$companyDirectoriesRef = null;
    this.$computeDirectorySourcesTimeout = null;
    this.$controllers = null;
    this.$directoriesByCompany = null;
    this.$directoriesByCompanyRef = null;
    this.$directorySources = null;
    this.$equipment = null; // only use for computing directory sources

    FirebaseObject.call(this, KayleeBase.app.database().ref('rooms/' + roomId));
}

Room.prototype = Object.create(FirebaseObject.prototype);
Room.prototype.constructor = Room;

Room.prototype.$defaults = {
    caps: {
        disabledCalls: {
            googleMeet: 0,
            pstn: 0,
            teams: 0,
            video: 0,
            voip: 0,
            zoom: 0,
        },
        hasMicrophoneMute: false,
        hasRoomVolume: false,
        hasRoomVolumeMute: false,
        maxCalls: {
            googleMeet: 0,
            pstn: 0,
            teams: 0,
            video: 0,
            voip: 0,
            zoom: 0,
        },
    },
    configuredCaps: {
        disabledCalls: {
            googleMeet: 0,
            pstn: 0,
            teams: 0,
            video: 0,
            voip: 0,
            zoom: 0,
        },
        hasMicrophoneMute: false,
        hasRoomVolume: false,
        hasRoomVolumeMute: false,
        maxCalls: {
            googleMeet: 0,
            pstn: 0,
            teams: 0,
            video: 0,
            voip: 0,
            zoom: 0,
        },
    },
    settings: {
        o365CalendarEnabled: defaults.inara.o365CalendarEnabled,
        o365User: 'someone@example.com',
        roomControlApiTcpSocketEnabled: false,
        userPasswordTimeoutSec: 0,
    },
    states: {
        mute: false,
        nearCameraPosition: { x: 0, y: 0, z: 0 },
        nearCameraTracking: false,
        onAir: false,
        onlineState: 'offline',
        videoInputSource: '1',
        volume: 0,
    },
};

Room.prototype.$includeInChangedEvent = {
    name: true,
    settings: true,
    states: {
        onlineState: true,
    },
};

Room.prototype.$activateUiPreview = function (controllerId) {
    return this.$ref.child('uiPreview').set({ controllerId: controllerId, timestamp: Date.now() });
};

// Similar-looking characters removed: 0, o, O, 1, l, I
var apiKeyChars = '23456789abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ';

Room.prototype.$addApiKey = function (apiKeyInfo, apiKey) {
    if (apiKey === null || typeof apiKey === 'undefined') {
        apiKey = sutl.generateRandomString(20, apiKeyChars);
    }

    var updates = {};
    updates['/roomApiKeys/' + this.$id + '/' + apiKey] = apiKeyInfo;
    return KayleeEvent.addUpdatesForRoomApiKeyCreatedEvent(
        updates,
        this.$id,
        apiKey,
        apiKeyInfo.description
    )
        .then(function () {
            return KayleeBase.update(updates);
        })
        .then(function () {
            return apiKey;
        });
};

Room.prototype.$removeApiKey = function (apiKey) {
    var self = this;
    var updates = {};
    updates['/roomApiKeys/' + this.$id + '/' + apiKey] = null;

    return KayleeUtil.loadRoomApiKeyInfoData({ roomId: this.$id, apiKey: apiKey })
        .then(function (obj) {
            return KayleeEvent.addUpdatesForRoomApiKeyDeletedEvent(
                updates,
                self.$id,
                apiKey,
                obj.roomApiKeyInfo.description
            );
        })
        .then(function () {
            return KayleeBase.update(updates);
        });
};

Room.prototype.$updateApiKey = function (apiKey, description) {
    var updates = { description: description };
    return KayleeBase.update('/roomApiKeys/' + this.$id + '/' + apiKey, updates);
};

Room.prototype.$addCall = function (callInfo) {
    var self = this;

    if (KayleeConnectionState.isWorkingOnline()) {
        return KayleeCall.addUpdatesToCreateCall(
            {},
            self.companyId,
            self.siteId,
            self.$id,
            callInfo.callId,
            callInfo
        )
            .then(function (update) {
                return KayleeBase.update(update);
            })
            .then(function () {
                log.info('added [Call (' + callInfo.callId + ')] to ' + self);
                return callInfo.callId;
            });
    }

    // FIXME: This implementation is fragile. It assumes everyone will always
    // use defaults.inara.numRecentCalls as the limit. This was added for
    // SNTY-1306.

    var calls = this.$getCalls(defaults.inara.numRecentCalls);
    calls.$addNewCallWhileWorkingOffline(callInfo);
    return Promise.resolve(callInfo.callId);
};

Room.prototype.$addEquipment = function (
    typeIdOrCallback,
    modelNumberId,
    ipAddress,
    username,
    password,
    simulate,
    config
) {
    var equipmentId = this.$ref.push().key;

    if (typeof typeIdOrCallback === 'function') {
        var info = typeIdOrCallback(equipmentId);

        return this.$internalAddEquipment(
            equipmentId,
            info.typeId,
            info.modelNumberId,
            info.ipAddress,
            info.username,
            info.password,
            info.simulate,
            info.config
        );
    }

    return this.$internalAddEquipment(
        equipmentId,
        typeIdOrCallback,
        modelNumberId,
        ipAddress,
        username,
        password,
        simulate,
        config
    );
};

Room.prototype.$addUpdatesForChangedEvent = KayleeEvent.addUpdatesForRoomChangedEvent;

Room.prototype.$companyDirectoriesChangedCb = function (snap) {
    this.$computeDirectorySourcesDelayed();
};

Room.prototype.$computeDirectorySources = function () {
    var availableCompanyDirectoryIds = Object.keys(this.$directoriesByCompany);
    var enabledCompanyDirectoryIds = Object.keys(
        sutl.getNode(this, ['settings', 'companyDirectories'], {})
    );
    var availableEqDirectoryIds = [];
    var enabledEqDirectoryIds = [];

    // Compute available/enabled equipment directory info.

    this.$equipment.forEach(function (eq) {
        if (eq.$hasDirectory()) {
            availableEqDirectoryIds.push(eq.$id);
            if (sutl.getNode(eq, ['config', 'useDirectory'], false)) {
                enabledEqDirectoryIds.push(eq.$id);
            }
        }
    }, this);

    // Remove directories that no longer exist.

    for (var i = 0; i < this.$directorySources.length; i++) {
        var src = this.$directorySources[i];

        if (
            (src.type === 'company' && !availableCompanyDirectoryIds.includes(src.id)) ||
            (src.type === 'equipment' && !availableEqDirectoryIds.includes(src.id))
        ) {
            this.$directorySources.splice(i, 1);
            i--;
        }
    }

    // Add new company directories.

    availableCompanyDirectoryIds.forEach(function (id) {
        if (this.$getDirectorySourceById(id) === null) {
            this.$directorySources.push({
                type: 'company',
                id: id,
                selected: false,
            });
        }
    }, this);

    // Add new equipment directories.

    availableEqDirectoryIds.forEach(function (id) {
        if (this.$getDirectorySourceById(id) === null) {
            this.$directorySources.push({
                type: 'equipment',
                id: id,
                selected: false,
            });
        }
    }, this);

    // Update selected property for company directories.

    this.$directorySources.forEach(function (src) {
        if (src.type === 'company') {
            src.selected = enabledCompanyDirectoryIds.includes(src.id);
        } else if (src.type === 'equipment') {
            src.selected = enabledEqDirectoryIds.includes(src.id);
        }
    }, this);

    FirebaseExt.update();
};

Room.prototype.$computeDirectorySourcesDelayed = function () {
    if (this.$computeDirectorySourcesTimeout !== null) {
        KayleeUtil.clearTimeout(this.$computeDirectorySourcesTimeout);
    }

    this.$computeDirectorySourcesTimeout = KayleeUtil.setTimeout(
        this.$onComputeDirectorySourcesTimeout.bind(this),
        1000
    );
};

Room.prototype.$deactivateUiPreview = function () {
    return this.$ref.child('uiPreview').set(null);
};

Room.prototype.$destroy = function (error) {
    FirebaseObject.prototype.$destroy.call(this, error);

    if (this.$computeDirectorySourcesTimeout !== null) {
        KayleeUtil.clearTimeout(this.$computeDirectorySourcesTimeout);
        this.$computeDirectorySourcesTimeout = null;
    }

    if (this.$directoriesByCompanyRef !== null) {
        this.$directoriesByCompanyRef.off('value', this.$directoriesByCompanyChangedCb, this);
    }

    if (this.$companyDirectoriesRef !== null) {
        this.$companyDirectoriesRef.off('value', this.$companyDirectoriesChangedCb, this);
    }

    if (this.$equipment !== null && this.$equipment.$isLoaded()) {
        this.$equipment.$offChildAdded(this.$equipmentAddedCb, this);
        this.$equipment.$offChildChanged(this.$equipmentChangedCb, this);
        this.$equipment.$offChildRemoved(this.$equipmentRemovedCb, this);
    }
};

Room.prototype.$directoriesByCompanyChangedCb = function (snap) {
    this.$directoriesByCompany = snap.exists() ? snap.val() : {};
    this.$computeDirectorySourcesDelayed();
};

Room.prototype.$disableOffice365Calendar = function () {
    return KayleeBase.request('office365DisableCalendarRequest', {
        roomId: this.$id,
    });
};

Room.prototype.$disableSelectedCalendarSource = function () {
    var self = this;

    return self
        .$getEquipment()
        .$loaded()
        .then(function (equipment) {
            var promises = [];
            var office365CalendarId = KayleeOffice365.getEnabledCalendarId(self);

            // There should only be one calendar enabled, but we check all of them
            // anyway.

            if (office365CalendarId !== null) {
                promises.push(self.$disableOffice365Calendar(office365CalendarId));
            }

            for (var i = 0; i < equipment.length; i++) {
                if (sutl.getNode(equipment[i], ['config', 'useCalendar'], false)) {
                    equipment[i].config.useCalendar = false;
                    promises.push(equipment[i].$save());
                }
            }

            if (promises.length > 0) {
                delete self.settings.calendarSource;
                promises.push(self.$save());
            }

            return Promise.all(promises);
        });
};

// $discoverEquipment(equipmentTypeId: string, modelNumberId: string | undefined): Promise<EquipmentDiscoveryResponse | undefined>;

Room.prototype.$discoverEquipment = function (equipmentTypeId, modelNumberId) {
    var timeoutMs = KayleeEquipment.getEqTypeConfigProp(
        equipmentTypeId,
        modelNumberId,
        'equipmentDiscoveryTimeoutMs'
    );
    if (typeof timeoutMs === 'undefined' || timeoutMs === null) timeoutMs = 20000;

    var driverId = KayleeEquipment.getDriverId(equipmentTypeId, modelNumberId);
    if (driverId === undefined) return Promise.resolve(undefined);

    var time = Date.now();

    var roomDiscoveredEquipmentDeferredPromise = {};

    roomDiscoveredEquipmentDeferredPromise.promise = new Promise(function (resolve, reject) {
        roomDiscoveredEquipmentDeferredPromise.resolve = resolve;
        roomDiscoveredEquipmentDeferredPromise.reject = reject;
    });

    var roomDiscoveredEquipment = KayleeRoomDiscoveredEquipment.getRoomDiscoveredEquipment(
        this.$id
    );

    var onRoomDiscoveredEquipmentChanged = function () {
        var discoveredEquipment = sutl.getNode(
            roomDiscoveredEquipment,
            ['responses', driverId],
            undefined
        );

        var discoveredTime = sutl.getNode(discoveredEquipment, ['time'], 0);

        if (discoveredTime >= time) {
            roomDiscoveredEquipmentDeferredPromise.resolve(discoveredEquipment);
        }
    };

    roomDiscoveredEquipment.$onChange(onRoomDiscoveredEquipmentChanged);

    this.$requestDiscoverEquipment(driverId);

    var timeout = KayleeUtil.timeout('timeout', timeoutMs);

    return Promise.race([roomDiscoveredEquipmentDeferredPromise.promise, timeout]).then(function (
        result
    ) {
        roomDiscoveredEquipment.$offChange(onRoomDiscoveredEquipmentChanged);
        if (result === 'timeout') {
            var discoveredEquipment = sutl.getNode(
                roomDiscoveredEquipment,
                ['responses', driverId],
                undefined
            );

            return discoveredEquipment;
        }

        return result;
    });
};

Room.prototype.$enableDirectorySource = function (type, id, enable) {
    var update = {};

    if (type === 'company') {
        update['rooms/' + this.$id + '/settings/companyDirectories/' + id] = enable ? true : null;
        return KayleeBase.update(update);
    } else if (type === 'equipment') {
        update['equipment/' + id + '/config/useDirectory'] = enable;
        return KayleeBase.update(update);
    }

    return Promise.reject(new Error('invalid type: ' + type));
};

Room.prototype.$enableEquipmentCalendar = function (id) {
    var self = this;
    return self
        .$getEquipment()
        .$loaded()
        .then(function (equipment) {
            for (var i = 0; i < equipment.length; i++) {
                var eq = equipment[i];

                if (eq.$id !== id) continue;

                sutl.setNode(eq, ['config', 'useCalendar'], true);
                return eq.$save();
            }

            return Promise.resolve();
        });
};

Room.prototype.$enableOffice365Calendar = function (calendarId) {
    return KayleeBase.request('office365EnableCalendarRequest', {
        roomId: this.$id,
        calendarId: calendarId,
    });
};

Room.prototype.$equipmentAddedCb = function (id) {
    this.$computeDirectorySourcesDelayed();
};

Room.prototype.$equipmentChangedCb = function (id) {
    this.$computeDirectorySourcesDelayed();
};

Room.prototype.$equipmentRemovedCb = function (id) {
    this.$computeDirectorySourcesDelayed();
};

Room.prototype.$getApiKeys = function () {
    return KayleeRoomApiKeys.getRoomApiKeys(this.$id);
};

Room.prototype.$getCalendar = function () {
    return KayleeCalendar.getCalendar(this.$id);
};

Room.prototype.$getCalendarSources = function () {
    var self = this;
    var sources = [];
    var equipment = null;
    var selected = null;
    var err;
    var i;

    return KayleeUtil.loadCompanyData({ companyId: self.companyId })
        .then(function (obj) {
            // See if Office 365 is successfully enabled for the company. If it is,
            // then request the available calendars and add the to the sources.

            if (KayleeOffice365.getAuthorizationStatus(obj.company) !== 'ok') {
                return Promise.resolve();
            }

            return KayleeBase.request('office365CalendarsRequest', {
                companyId: self.companyId,
                siteId: self.siteId,
            }).then(function (office365Calendars) {
                for (var id in office365Calendars) {
                    if (!office365Calendars.hasOwnProperty(id)) continue;
                    var source = JSON.parse(JSON.stringify(office365Calendars[id]));
                    source.type = 'office365';
                    source.selected = KayleeOffice365.getEnabledCalendarId(self) === source.id;
                    sources.push(source);
                }

                return Promise.resolve();
            });
        })
        .then(function () {
            // Load the equipment in the room.

            equipment = self.$getEquipment();
            return equipment.$loaded();
        })
        .then(function (e) {
            // If any of the equipment in the room is capable of sourcing a
            // calendar, then add it to the sources.

            for (i = 0; i < equipment.length; i++) {
                var eq = equipment[i];
                if (eq.$hasCalendar()) {
                    sources.push({
                        type: 'equipment',
                        id: eq.$id,
                        selected: sutl.getNode(eq, ['config', 'useCalendar'], false),
                    });
                }
            }

            return Promise.resolve();
        })
        .then(function () {
            // Verify that only one source is selected.

            var numSelected = 0;

            for (i = 0; i < sources.length; i++) {
                if (sources[i].selected) {
                    numSelected++;
                    selected = sources[i];
                }
            }

            if (numSelected > 1) {
                err = new Error(
                    numSelected +
                        ' calendar sources selected; should be 0 or 1: ' +
                        JSON.stringify(sources, null, 4)
                );
                log.error(err.message);
                return Promise.reject(err);
            }

            return Promise.resolve();
        })
        .then(function () {
            // Verify that the calendar we detected as selected matches the room
            // setting for the selected calendar.

            var calendarSource = sutl.getNode(self, ['settings', 'calendarSource']);

            if (
                calendarSource === null ||
                selected === null ||
                (calendarSource.type === selected.type && calendarSource.id === selected.id)
            ) {
                return Promise.resolve();
            }

            err = new Error(
                "detected calendar selection doesn't match room settings: " +
                    'detected = ' +
                    JSON.stringify(selected, null, 4) +
                    ', room settings = ' +
                    JSON.stringify(calendarSource, null, 4)
            );
            log.error(err.message);
            return Promise.reject(err);
        })
        .then(function () {
            return Promise.resolve(sources);
        });
};

Room.prototype.$getCalendarStatus = function () {
    var calendarSource = sutl.getNode(this, ['settings', 'calendarSource']);

    if (calendarSource === null) {
        return null;
    } else if (calendarSource.type === 'equipment') {
        return 'ok';
    } else if (calendarSource.type === 'office365') {
        return this.$getOffice365CalendarStatus();
    }

    log.error('invalid calendar type: ' + calendarSource.type);
    return null;
};

Room.prototype.$getCalls = function (limit) {
    return KayleeCalls.getCalls(this, limit);
};

Room.prototype.$getCommands = function () {
    return KayleeRoomControlCommands.getRoomControlCommands(this.$id);
};

Room.prototype.$getCompanyDirectories = function () {
    return KayleeDirectories.getDirectories(this);
};

Room.prototype.$getControllers = function () {
    return KayleeControllers.getControllers(this);
};

Room.prototype.$getControllerCount = function () {
    if (!this.$internalIsLoaded) return 0;

    if (this.$controllers === null) {
        this.$controllers = this.$getControllers();
    }

    return this.$controllers.length;
};

Room.prototype.$getDirectory = function () {
    return KayleeDirectory.getDirectory(this.$id);
};

Room.prototype.$getDirectorySourceById = function (id) {
    if (this.$directorySources === null) return null;

    for (var i = 0; i < this.$directorySources.length; i++) {
        if (this.$directorySources[i].id === id) return id;
    }

    return null;
};

Room.prototype.$getDirectorySources = function () {
    if (this.$directorySources !== null) {
        return Promise.resolve(this.$directorySources);
    }

    return this.$loadDirectorySources();
};

// Example:
//
//     var driverId = KayleeEquipment.getDriverId(equipmentTypeId, modelNumberId);
//     room.$getDiscoveredEquipment().$loaded().then(function(discoveredEquipment) {
//         var response = sutl.getNode(discoveredEquipment, ['responses', driverId]);
//
//         if (response) {
//             console.log('equipment discovered at: ' + response.time);
//             console.log('discovered equipment: ' + JSON.stringify(response.equipment));
//         } else {
//             console.log('no discovered equipment');
//         }
//     });
//

Room.prototype.$getDiscoveredEquipment = function () {
    return KayleeRoomDiscoveredEquipment.getRoomDiscoveredEquipment(this.$id);
};

Room.prototype.$getEquipment = function () {
    return KayleeEquipments.getEquipments(this);
};

Room.prototype.$getEvents = function (limit) {
    return KayleeEvents.getEvents(this, limit);
};

Room.prototype.$getRemoteControlSessions = function () {
    return KayleeRemoteControlSessions.getRemoteControlSessions(this.$id);
};

Room.prototype.$getSelectedCalendarSourceInfo = function () {
    var calendarSource = sutl.getNode(this, ['settings', 'calendarSource']);

    if (calendarSource === null) return null;

    calendarSource = JSON.parse(JSON.stringify(calendarSource));

    if (calendarSource.type === 'office365') {
        if (KayleeOffice365.calendarIsEnabled(this)) {
            calendarSource.name = KayleeOffice365.getEnabledCalendarName(this);
            return calendarSource;
        }

        return null;
    } else if (calendarSource.type === 'equipment') {
        return calendarSource;
    }

    log.error('invalid calendar type: ' + calendarSource.type);
    return null;
};

Room.prototype.$getOffice365CalendarStatus = function () {
    return KayleeOffice365.getCalendarStatus(this);
};

Room.prototype.$getUsers = function () {
    return KayleeUsers.getUsers(this);
};

Room.prototype.$hasAlert = function () {
    var calendarStatus = this.$getCalendarStatus();
    return (
        this.$isAnyControllerOffline() ||
        this.$isAnyEquipmentOffline() ||
        this.$isAnyControllerInSetup() ||
        (calendarStatus !== null && calendarStatus !== 'ok')
    );
};

Room.prototype.$internalAddEquipment = function (
    equipmentId,
    typeId,
    modelNumberId,
    ipAddress,
    username,
    password,
    simulate,
    config
) {
    var self = this;

    return KayleeEquipment.addUpdatesToCreateEquipment(
        {},
        self.companyId,
        self.siteId,
        self.$id,
        equipmentId,
        typeId,
        modelNumberId,
        ipAddress,
        username,
        password,
        simulate,
        config
    )
        .then(function (update) {
            return KayleeBase.update(update);
        })
        .then(function () {
            log.info('added [Equipment (' + equipmentId + ')] to ' + self);
            return equipmentId;
        });
};

Room.prototype.$isAnyControllerInSetup = function () {
    if (!this.$internalIsLoaded) return false;

    if (this.$controllers === null) {
        this.$controllers = this.$getControllers();
    }

    if (this.$controllers.length === 0) return false;

    for (var i = 0; i < this.$controllers.length; i++) {
        if (this.$controllers[i].isInSetup) return true;
    }

    return false;
};

Room.prototype.$isAnyControllerOffline = function () {
    if (!this.$internalIsLoaded) return false;

    if (this.$controllers === null) {
        this.$controllers = this.$getControllers();
    }

    if (this.$controllers.length === 0) return false;

    for (var i = 0; i < this.$controllers.length; i++) {
        if (!this.$controllers[i].dbConnected) return true;
    }

    return false;
};

Room.prototype.$isAnyEquipmentOffline = function () {
    return this.$isConnectedToDb() && this.states.onlineState !== 'online';
};

Room.prototype.$isConnectedToDb = function () {
    if (!this.$internalIsLoaded) return false;

    if (this.$controllers === null) {
        this.$controllers = this.$getControllers();
    }

    if (this.$controllers.length === 0) return false;

    for (var i = 0; i < this.$controllers.length; i++) {
        if (this.$controllers[i].dbConnected) return true;
    }

    return false;
};

Room.prototype.$isInCall = function () {
    return (this.$isConnectedToDb() || this.$isUiPreviewActive()) && this.states.onAir;
};

Room.prototype.$isUiPreviewActive = function () {
    return (
        Date.now() - sutl.getNode(this, ['uiPreview', 'timestamp'], 0) <
        defaults.inara.uiPreviewTimeoutMs + 2000
    );
};

Room.prototype.$loadDirectorySources = function () {
    var self = this;

    self.$directorySources = [];

    return self
        .$loaded()
        .then(function () {
            self.$directoriesByCompanyRef = KayleeBase.app
                .database()
                .ref('index/directoriesByCompany/' + self.companyId);
            self.$companyDirectoriesRef = KayleeBase.app
                .database()
                .ref('rooms/' + self.$id + '/settings/companyDirectories');

            var deferredPromise = {};

            deferredPromise.promise = new Promise(function (resolve, reject) {
                deferredPromise.resolve = resolve;
                deferredPromise.reject = reject;
            });

            self.$directoriesByCompanyRef.once('value', function (snap) {
                deferredPromise.resolve(snap.exists() ? snap.val() : {});
            });

            return deferredPromise.promise;
        })
        .then(function (directoriesByCompany) {
            self.$directoriesByCompany = directoriesByCompany;
            return self.$getEquipment().$loaded();
        })
        .then(function (equipment) {
            self.$equipment = equipment;

            self.$directoriesByCompanyRef.on('value', self.$directoriesByCompanyChangedCb, self);
            self.$companyDirectoriesRef.on('value', self.$companyDirectoriesChangedCb, self);

            equipment.$onChildAdded(self.$equipmentAddedCb, self);
            equipment.$onChildChanged(self.$equipmentChangedCb, self);
            equipment.$onChildRemoved(self.$equipmentRemovedCb, self);

            self.$computeDirectorySources();

            return self.$directorySources;
        });
};

Room.prototype.$onComputeDirectorySourcesTimeout = function () {
    this.$computeDirectorySourcesTimeout = null;
    this.$computeDirectorySources();
};

// Pair a controller with the specified pairing code to this room. Returns a
// promise that resolves when the pairing response has been recieved from Jayne.

Room.prototype.$pair = function (pairingCode) {
    return KayleeBase.request('pairingRequest', {
        time: Date.now(),
        pairingCode: pairingCode,
        roomId: this.$id,
    });
};

Room.prototype.$pushCommand = function (command) {
    command.time = Date.now();

    if (KayleeConnectionState.isConnected() && KayleeConnectionState.isWorkingOnline()) {
        return this.$commandsRef
            .push(command)
            .then(function (ref) {
                return ref.key;
            })
            .catch(function (err) {
                log.error('$pushCommand failed: ' + err);
                log.debug('command = ' + JSON.stringify(command, null, 4));
                return Promise.reject(err);
            });
    } else {
        this.$commandsRef.push(command).then(function (ref) {
            return ref.key;
        });

        return Promise.resolve();
    }
};

Room.prototype.$remove = function () {
    var self = this;
    var update = {};

    if (!self.$internalIsLoaded) {
        var err = new Error(self + ' not loaded');
        log.error(err.message);
        return Promise.reject(err);
    }

    return addUpdatesToRemoveRoom(update, self.companyId, self.siteId, self.$id)
        .then(function (update) {
            return KayleeBase.update(update);
        })
        .then(function () {
            log.info('removed ' + self);
        });
};

Room.prototype.$requestAnswer = function (callId) {
    return this.$pushCommand({
        type: 'answer',
        callId: callId.toString(),
    });
};

Room.prototype.$requestAssistiveListeningMute = function (value) {
    return this.$pushCommand({
        type: 'assistiveListeningMute',
        value: value ? true : false,
    });
};

Room.prototype.$requestAssistiveListeningMuteToggle = function () {
    return this.$pushCommand({
        type: 'assistiveListeningMuteToggle',
    });
};

Room.prototype.$requestAssistiveListeningVolumeInc = function (value) {
    return this.$pushCommand({
        type: 'assistiveListeningVolumeInc',
        value: parseInt(value, 10),
    });
};

Room.prototype.$requestAssistiveListeningVolume = function (value) {
    return this.$pushCommand({
        type: 'assistiveListeningVolume',
        value: parseInt(value, 10),
    });
};

Room.prototype.$requestAudioInputMute = function (srcId, value) {
    return this.$pushCommand({
        type: 'audioInputMute',
        srcId: srcId,
        value: value ? true : false,
    });
};

Room.prototype.$requestAudioInputMuteToggle = function (srcId) {
    return this.$pushCommand({
        type: 'audioInputMuteToggle',
        srcId: srcId,
    });
};

Room.prototype.$requestAudioInputVolume = function (srcId, value) {
    return this.$pushCommand({
        type: 'audioInputVolume',
        srcId: srcId,
        value: parseInt(value, 10),
    });
};

Room.prototype.$requestAudioInputVolumeInc = function (srcId, value) {
    return this.$pushCommand({
        type: 'audioInputVolumeInc',
        srcId: srcId,
        value: parseInt(value, 10),
    });
};

Room.prototype.$requestCall = function (callMethod) {
    var cmd = Object.assign({ type: 'call' }, callMethod);

    if (cmd.hasOwnProperty('invite') && Array.isArray(cmd.invite)) {
        var invite = {};

        for (var i = 0; i < cmd.invite.length; i++) {
            invite['a' + i] = cmd.invite[i];
        }

        cmd.invite = invite;
    }

    if (cmd.hasOwnProperty('callClass')) {
        delete cmd.callClass;
    }

    return this.$pushCommand(cmd);
};

Room.prototype.$requestCallLayout = function (callId, layoutId) {
    return this.$pushCommand({
        type: 'callLayout',
        callId: callId,
        value: layoutId,
    });
};

Room.prototype.$requestCameraMove = function (cameraId, info, holdRepeatMs) {
    var cmd = {
        type: 'cameraMove',
        cameraId: cameraId,
    };

    if (info.hasOwnProperty('action')) {
        cmd.direction = info.direction;
        cmd.action = info.action;
    }

    if (info.hasOwnProperty('x')) {
        cmd.value = {
            x: parseInt(info.x, 10),
            y: parseInt(info.y, 10),
            z: parseInt(info.z, 10),
        };
    }

    if (typeof holdRepeatMs === 'number') {
        cmd.holdRepeatMs = holdRepeatMs;
    }

    return this.$pushCommand(cmd);
};

// $requestCameraPosition: note cameraId is the Visca ID for the camera, not
// the database key. Also, this method is different from the other $request*
// commands in that it returns a promise that resolves with the result.

Room.prototype.$requestCameraPosition = function (equipmentId, cameraId) {
    var requestId = KayleeBase.getNewKey();

    var deferredPromise = {};
    var path = 'equipmentStates/' + equipmentId + '/cameraPositionResults/' + requestId;
    var ref = KayleeBase.app.database().ref(path);

    deferredPromise.promise = new Promise(function (resolve, reject) {
        deferredPromise.resolve = resolve;
        deferredPromise.reject = reject;
    });

    var cb = ref.on(
        'value',
        function (snap) {
            if (snap.exists()) {
                ref.off('value', cb);
                deferredPromise.resolve(snap.val());
            }
        },
        function (err) {
            deferredPromise.reject(err);
        }
    );

    this.$pushCommand({
        type: 'cameraPosition',
        equipmentId: equipmentId,
        cameraId: cameraId,
        requestId: requestId,
    });

    return deferredPromise.promise;
};

Room.prototype.$requestCameraPreset = function (cameraSwitcherId, presetId) {
    this.$pushCommand({
        type: 'cameraPreset',
        cameraSwitcherId: cameraSwitcherId,
        presetId: presetId,
    });
};

Room.prototype.$requestCameraSelectByName = function (cameraSwitcherId, cameraName) {
    return this.$pushCommand({
        type: 'cameraSelectByName',
        cameraSwitcherId: cameraSwitcherId,
        cameraName: cameraName,
    });
};

Room.prototype.$requestCameraSelected = function (cameraId) {
    return this.$pushCommand({
        type: 'cameraSelected',
        cameraId: cameraId,
    });
};

Room.prototype.$requestCameraTracking = function (cameraId, value) {
    return this.$pushCommand({
        type: 'cameraTracking',
        cameraId: cameraId,
        value: value ? true : false,
    });
};

Room.prototype.$requestConsentToRecording = function (callId, consent) {
    return this.$pushCommand({
        type: 'consentToRecording',
        callId: callId,
        value: consent,
    });
};

Room.prototype.$requestDial = function (callId, dialString) {
    return this.$pushCommand({
        type: 'dial',
        callId: callId.toString(),
        dialString: dialString.toString(),
    });
};

Room.prototype.$requestDiscoverEquipment = function (driverId) {
    return this.$pushCommand({
        type: 'discoverEquipment',
        driverId: driverId.toString(),
    });
};

Room.prototype.$requestDisplayBlank = function (displayId, value) {
    return this.$pushCommand({
        type: 'displayBlank',
        displayId: displayId.toString(),
        value: value ? true : false,
    });
};

Room.prototype.$requestDisplayFreeze = function (displayId, value) {
    return this.$pushCommand({
        type: 'displayFreeze',
        displayId: displayId.toString(),
        value: value ? true : false,
    });
};

Room.prototype.$requestDisplayVolume = function (displayId, value) {
    return this.$pushCommand({
        type: 'displayVolume',
        displayId: displayId.toString(),
        value: value,
    });
};

Room.prototype.$requestDisplayVolumeInc = function (displayId, value) {
    return this.$pushCommand({
        type: 'displayVolumeInc',
        displayId: displayId.toString(),
        value: value,
    });
};

Room.prototype.$requestDisplayVolumeMute = function (displayId, value) {
    return this.$pushCommand({
        type: 'displayVolumeMute',
        displayId: displayId.toString(),
        value: value,
    });
};

Room.prototype.$requestDisplayVolumeMuteToggle = function (displayId) {
    return this.$pushCommand({
        type: 'displayVolumeMuteToggle',
        displayId: displayId.toString(),
    });
};

Room.prototype.$requestDisplaySource = function (displayId, sourceId) {
    return this.$pushCommand({
        type: 'displaySource',
        displayId: displayId.toString(),
        value: sourceId.toString(),
    });
};

Room.prototype.$requestEquipmentSetting = function (equipmentId, name, value) {
    var cmd = {
        type: 'equipmentSetting',
        equipmentId: equipmentId,
        name: name,
    };

    if (typeof value !== 'undefined' && value !== null) {
        cmd.value = value;
    }

    return this.$pushCommand(cmd);
};

Room.prototype.$requestHangup = function (callId, endMeeting) {
    var cmd = {
        type: 'hangup',
        callId: callId.toString(),
    };

    if (typeof endMeeting !== 'undefined' && endMeeting !== null) {
        cmd.endMeeting = endMeeting;
    }

    return this.$pushCommand(cmd);
};

Room.prototype.$requestHideDisplay = function (displayId, displayMotionId, manual) {
    var cmd = {
        type: 'hideDisplay',
        displayId: displayId,
        displayMotionId: displayMotionId,
    };

    if (typeof manual !== 'undefined') cmd.manual = manual;

    return this.$pushCommand(cmd);
};

Room.prototype.$requestInviteToCall = function (callId, invitees) {
    var cmd = {
        type: 'inviteToCall',
        callId: callId.toString(),
    };

    if (Array.isArray(invitees)) {
        cmd.invitees = {};

        for (var i = 0; i < invitees.length; i++) {
            cmd.invitees['a' + i] = invitees[i];
        }
    } else {
        cmd.invitees = invitees;
    }

    return this.$pushCommand(cmd);
};

Room.prototype.$requestLightingLevel = function (lightingControlId, level) {
    return this.$pushCommand({
        type: 'lightingLevel',
        lightingControlId: lightingControlId,
        value: level,
    });
};

Room.prototype.$requestLightingLevelInc = function (lightingControlId, value) {
    return this.$pushCommand({
        type: 'lightingLevelInc',
        lightingControlId: lightingControlId,
        value: value,
    });
};

Room.prototype.$requestLightingPower = function (lightingControlId, power) {
    return this.$pushCommand({
        type: 'lightingPower',
        lightingControlId: lightingControlId,
        value: power,
    });
};

Room.prototype.$requestLightingPowerToggle = function (lightingControlId) {
    return this.$pushCommand({
        type: 'lightingPowerToggle',
        lightingControlId: lightingControlId,
    });
};

Room.prototype.$requestLightingPreset = function (lightingPresetId) {
    return this.$pushCommand({
        type: 'lightingPreset',
        lightingPresetId: lightingPresetId,
    });
};

Room.prototype.$requestMediaOperation = function (equipmentId, request) {
    var cmd = {
        type: 'mediaOperation',
        equipmentId: equipmentId,
        videoInputId: request.videoInputId,
        operation: request.operation,
    };

    if (request.hasOwnProperty('channel')) {
        cmd.channel = request.channel;
    }

    if (request.hasOwnProperty('channelTuner')) {
        cmd.channelTuner = request.channelTuner;
    }

    if (request.hasOwnProperty('value')) {
        cmd.value = request.value;
    }

    return this.$pushCommand(cmd);
};

Room.prototype.$requestRoomMute = function (value) {
    return this.$pushCommand({
        type: 'roomMute',
        value: value ? true : false,
    });
};

Room.prototype.$requestRoomMuteToggle = function (value) {
    return this.$pushCommand({
        type: 'roomMuteToggle',
        value: value ? true : false,
    });
};

Room.prototype.$requestMuteParticipant = function (callId, participantId, value) {
    return this.$pushCommand({
        type: 'muteParticipant',
        callId: callId,
        participantId: participantId,
        value: value,
    });
};

Room.prototype.$requestMuteParticipantVideo = function (callId, participantId, value) {
    return this.$pushCommand({
        type: 'muteParticipantVideo',
        callId: callId,
        participantId: participantId,
        value: value,
    });
};

Room.prototype.$requestOverflowRoomMute = function (roomId, value) {
    return this.$pushCommand({
        type: 'overflowRoomMute',
        roomId: roomId,
        value: value ? true : false,
    });
};

Room.prototype.$requestOverflowRoomMuteToggle = function (roomId) {
    return this.$pushCommand({
        type: 'overflowRoomMuteToggle',
        roomId: roomId,
    });
};

Room.prototype.$requestOverflowRoomVolume = function (roomId, value) {
    return this.$pushCommand({
        type: 'overflowRoomVolume',
        roomId: roomId,
        value: parseInt(value, 10),
    });
};

Room.prototype.$requestOverflowRoomVolumeInc = function (roomId, value) {
    return this.$pushCommand({
        type: 'overflowRoomVolumeInc',
        roomId: roomId,
        value: parseInt(value, 10),
    });
};

Room.prototype.$requestPower = function (eqId, power) {
    return this.$pushCommand({
        type: 'power',
        eqId: eqId.toString(),
        value: power ? true : false,
    });
};

Room.prototype.$requestPowerToggle = function (eqId, power) {
    return this.$pushCommand({
        type: 'powerToggle',
        eqId: eqId.toString(),
    });
};

Room.prototype.$requestReboot = function (eqId) {
    return this.$pushCommand({
        type: 'reboot',
        eqId: eqId,
    });
};

Room.prototype.$requestReceiveGain = function (callId, value) {
    return this.$pushCommand({
        type: 'receiveGain',
        callId: callId,
        value: parseInt(value, 10),
    });
};

Room.prototype.$requestReject = function (callId) {
    return this.$pushCommand({
        type: 'reject',
        callId: callId.toString(),
    });
};

Room.prototype.$requestRoomOff = function () {
    return this.$pushCommand({
        type: 'roomOff',
    });
};

Room.prototype.$requestRunRoomMacro = function (macroId) {
    return this.$pushCommand({
        type: 'runRoomMacro',
        macroId: macroId,
    });
};

Room.prototype.$requestSelfView = function (cameraSwitcherId, value) {
    return this.$pushCommand({
        type: 'selfView',
        cameraSwitcherId: cameraSwitcherId,
        value: value,
    });
};

Room.prototype.$requestSelfViewAuto = function (cameraSwitcherId, value) {
    return this.$pushCommand({
        type: 'selfViewAuto',
        cameraSwitcherId: cameraSwitcherId,
        value: value,
    });
};

Room.prototype.$requestSelfViewPosition = function (cameraSwitcherId, value) {
    return this.$pushCommand({
        type: 'selfViewPosition',
        cameraSwitcherId: cameraSwitcherId,
        value: value,
    });
};

Room.prototype.$requestSendMeetingCode = function (callId, code) {
    return this.$pushCommand({
        type: 'sendMeetingCode',
        callId: callId,
        value: code,
    });
};

Room.prototype.$requestSendVideo = function (callId, send) {
    return this.$pushCommand({
        type: 'sendVideo',
        callId: callId,
        value: send,
    });
};

Room.prototype.$requestShadePosition = function (shadeControlId, motion, event) {
    return this.$pushCommand({
        type: 'shadePosition',
        shadeControlId: shadeControlId,
        motion: motion,
        event: event,
    });
};

Room.prototype.$requestShadePreset = function (shadePresetId) {
    return this.$pushCommand({
        type: 'shadePreset',
        shadePresetId: shadePresetId,
    });
};

Room.prototype.$requestShadeTilt = function (shadeControlId, motion, event) {
    return this.$pushCommand({
        type: 'shadeTilt',
        shadeControlId: shadeControlId,
        motion: motion,
        event: event,
    });
};

Room.prototype.$requestShowDisplay = function (displayId, displayMotionId, manual) {
    var cmd = {
        type: 'showDisplay',
        displayId: displayId,
        displayMotionId: displayMotionId,
    };

    if (typeof manual !== 'undefined') cmd.manual = manual;

    return this.$pushCommand(cmd);
};

Room.prototype.$requestSoundReinforcementMute = function (zoneId, value) {
    return this.$pushCommand({
        type: 'soundReinforcementMute',
        zoneId: zoneId,
        value: value ? true : false,
    });
};

Room.prototype.$requestSoundReinforcementMuteToggle = function (zoneId) {
    return this.$pushCommand({
        type: 'soundReinforcementMuteToggle',
        zoneId: zoneId,
    });
};

Room.prototype.$requestSoundReinforcementVolume = function (zoneId, value) {
    return this.$pushCommand({
        type: 'soundReinforcementVolume',
        zoneId: zoneId,
        value: parseInt(value, 10),
    });
};

Room.prototype.$requestSoundReinforcementVolumeInc = function (zoneId, value) {
    return this.$pushCommand({
        type: 'soundReinforcementVolumeInc',
        zoneId: zoneId,
        value: parseInt(value, 10),
    });
};

Room.prototype.$requestSpotlightParticipant = function (callId, participantId, value) {
    return this.$pushCommand({
        type: 'spotlightParticipant',
        callId: callId,
        participantId: participantId,
        value: value,
    });
};

Room.prototype.$requestStartInteractiveAuthentication = function (eqId) {
    return this.$pushCommand({
        type: 'startInteractiveAuthentication',
        eqId: eqId,
    });
};

Room.prototype.$requestStopContent = function (displayId, sourceId) {
    return this.$pushCommand({
        type: 'stopContent',
        displayId: displayId.toString(),
        value: sourceId.toString(),
    });
};

Room.prototype.$requestStopDisplay = function (displayId, displayMotionId, manual) {
    var cmd = {
        type: 'stopDisplay',
        displayId: displayId,
        displayMotionId: displayMotionId,
    };

    if (typeof manual !== 'undefined') cmd.manual = manual;

    return this.$pushCommand(cmd);
};

Room.prototype.$requestUsbHost = function (usbSwitcherId, hostId) {
    return this.$pushCommand({
        type: 'usbHost',
        usbSwitcherId: usbSwitcherId.toString(),
        value: hostId.toString(),
    });
};

Room.prototype.$requestRoomVolume = function (value) {
    return this.$pushCommand({
        type: 'roomVolume',
        value: parseInt(value, 10),
    });
};

Room.prototype.$requestRoomVolumeInc = function (value) {
    return this.$pushCommand({
        type: 'roomVolumeInc',
        value: parseInt(value, 10),
    });
};

Room.prototype.$requestWakeCameras = function () {
    return this.$pushCommand({
        type: 'wakeCameras',
    });
};

Room.prototype.$resetRemoteControlCode = function (logError) {
    return KayleeBase.request(
        'resetRemoteControlCode',
        {
            roomId: this.$id,
        },
        logError
    );
};

Room.prototype.$selectCalendarSource = function (type, id) {
    var self = this;

    return self
        .$disableSelectedCalendarSource()
        .then(function () {
            if (typeof type === 'undefined' || type === null) {
                return Promise.resolve();
            } else if (type === 'office365') {
                return self.$enableOffice365Calendar(id);
            } else if (type === 'equipment') {
                return self.$enableEquipmentCalendar(id);
            } else {
                return Promise.reject(new Error('invalid calendar type: ' + type));
            }
        })
        .then(function () {
            if (typeof type === 'undefined' || type === null) {
                delete self.settings.calendarSource;
            } else {
                self.settings.calendarSource = {
                    type: type,
                    id: id,
                };
            }

            return self.$save();
        });
};

Room.prototype.$setCalendar = function (calendar) {
    var self = this;
    var updates = {};
    updates['/calendars/' + this.$id] = calendar;

    return KayleeBase.update(updates).then(function () {
        log.info('updated calendar for ' + self);
    });
};

Room.prototype.$toJson = function () {
    return FirebaseObject.prototype.$toJson.call(this, ['$id', '$directorySources']);
};

Room.prototype.toString = function () {
    if (this.$internalIsLoaded) {
        return '[Room "' + this.name + '" (' + this.$id + ')]';
    }

    return '[Room (' + this.$id + ')]';
};

module.exports = KayleeRoom;
